export const formatQuestionBadge = (serialNumber) => {
  const sn = String(serialNumber ?? '');
  const match = sn.match(/^(\d+)/);
  return match ? `Question ${match[1]}` : sn ? `Question ${sn}` : 'Question';
};

export const formatQuestionLabel = (serialNumber) => {
  if (serialNumber == null) return 'Question';
  const sn = String(serialNumber ?? '');
  const match = sn.match(/^(\d+)([a-z])?$/i);
  if (match) {
    const num = match[1];
    const letter = match[2]?.toLowerCase();
    if (letter) {
      // Sub-question labels read "Question 1A", with no gap (JED-1277).
      return `Question ${num}${letter.toUpperCase()}`;
    }
    return `Question ${num}`;
  }
  return sn ? `Question ${sn}` : 'Question';
};

export const parseQuestionHtml = (html, hasSubQuestions = false, question = null) => {
  const explicitTitle = question?.title?.trim();
  if (explicitTitle) {
    return {
      title: explicitTitle,
      descriptionHtml: html || '',
      hasStructuredTitle: true,
    };
  }

  if (!html) {
    return { title: '', descriptionHtml: '', hasStructuredTitle: false };
  }

  if (typeof document === 'undefined') {
    return { title: '', descriptionHtml: html, hasStructuredTitle: false };
  }

  const root = document.createElement('div');
  root.innerHTML = html;
  const strong = root.querySelector('strong');

  if (strong) {
    const title = strong.textContent?.trim().replace(/:$/, '') || '';
    strong.remove();
    const descriptionHtml = root.innerHTML.trim();
    return {
      title,
      descriptionHtml,
      hasStructuredTitle: Boolean(title),
    };
  }

  if (hasSubQuestions) {
    const firstParagraph = root.querySelector('p');
    if (firstParagraph) {
      const title = firstParagraph.textContent?.trim() || '';
      firstParagraph.remove();
      return {
        title,
        descriptionHtml: root.innerHTML.trim(),
        hasStructuredTitle: Boolean(title),
      };
    }
  }

  return { title: '', descriptionHtml: html, hasStructuredTitle: false };
};

/**
 * Body html for an expanded parent question, which renders as an optional title
 * followed by exactly ONE body block. Rendering both the parsed description and
 * the raw question printed the parent stem twice.
 *
 * Which one to keep depends on whether a title was extracted:
 *
 * - Structured title: the title renders in its own block and descriptionHtml is
 *   the remainder, so show descriptionHtml. Showing the raw question here would
 *   repeat the title.
 * - No structured title: nothing renders the title block, and parsing may have
 *   DISCARDED content on the way to an empty title -- a leading paragraph holding
 *   only an image is stripped, and its textContent is empty, so the image is
 *   absent from descriptionHtml. The raw question is the complete source, so show
 *   that. In the common unstructured case descriptionHtml IS the raw question, so
 *   this costs nothing.
 */
export const parentQuestionBodyHtml = (parsed, rawQuestionHtml) => {
  if (parsed?.hasStructuredTitle) return parsed.descriptionHtml || '';
  return rawQuestionHtml || parsed?.descriptionHtml || '';
};

export const sortBySerialNumber = (questions) =>
  [...(questions || [])].sort((a, b) =>
    String(a.serialNumber).localeCompare(String(b.serialNumber), undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  );

export const getQuestionMarks = (question) => {
  if (!question) return null;
  // Don't count marks if showMarks is explicitly false
  if (question.showMarks === false) return null;

  if (question.marksOutOf != null) return question.marksOutOf;
  if (question.marks != null) return question.marks;
  if (question.totalMarks != null) return question.totalMarks;
  if (question.markingCriteria?.criterias?.length) {
    return question.markingCriteria.criterias.length;
  }
  if (question.shortAnswers?.length > 0 && question.shortAnswers[0].highestMarks) {
    return question.shortAnswers[0].highestMarks;
  }
  return null;
};

export const getParentMarks = (question, subQuestions) => {
  // Check if parent has marks (and showMarks is not false)
  const direct = getQuestionMarks(question);
  if (direct != null) return direct;

  // If parent doesn't have marks, sum child questions
  if (!subQuestions?.length) return null;
  const sum = subQuestions.reduce(
    (total, subQ) => {
      // Only count child marks if showMarks is not false
      const marks = getQuestionMarks(subQ);
      return total + (marks || 0);
    },
    0,
  );
  return sum || null;
};

const toFileSource = (file, idx) => ({
  key: file.id || `file-${idx}`,
  label: `Source ${idx + 1}`,
  url: file.url,
  fileType: file.fileType,
  kind: file.fileType === 'PDF' ? 'pdf' : 'image',
});

const toLegacySource = (source, idx) => ({
  key: source.id || `legacy-${idx}`,
  label: source.title || source.name || `Source ${idx + 1}`,
  url: source.url,
  fileType: source.fileType,
  kind: source.fileType === 'PDF' ? 'pdf' : 'image',
});

const getFileSources = (question) => {
  const files = Array.isArray(question?.stimulusFiles)
    ? question.stimulusFiles
    : [];
  if (files.length > 0) {
    return files.map(toFileSource);
  }
  return (question?.sources || question?.attachments || []).map(toLegacySource);
};

const hasStimulusText = (stimulusText) =>
  Boolean(stimulusText && String(stimulusText).replace(/<[^>]*>/g, '').trim());

const getTextSource = (question, offset) =>
  hasStimulusText(question?.stimulusText)
    ? [{ key: 'stimulus-text', label: `Source ${offset + 1}`, url: null, kind: 'text' }]
    : [];

export const getSourcesForQuestion = (question) => {
  if (!question) {
    return [];
  }
  const fileSources = getFileSources(question);
  return [...fileSources, ...getTextSource(question, fileSources.length)];
};
